/**
 * MODULE: _shared/frontend/lib/frontend/utils/fiscalCalendar.ts
 * PURPOSE: Tenant-aware fiscal year and quarter calculations (not calendar-year only)
 */

export type FiscalCalendarConfig = {
  /** Calendar month 1–12 when the fiscal year begins (1 = January). */
  fiscalYearStartMonth: number;
};

export const DEFAULT_FISCAL_CALENDAR: FiscalCalendarConfig = { fiscalYearStartMonth: 1 };

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function normalizeFiscalYearStartMonth(month: number | null | undefined): number {
  if (!month || month < 1 || month > 12) return 1;
  return month;
}

export function fiscalPeriodFromDate(
  referenceDate: Date,
  config: FiscalCalendarConfig = DEFAULT_FISCAL_CALENDAR
): { fiscalYear: number; fiscalQuarter: number } {
  const startMonth = normalizeFiscalYearStartMonth(config.fiscalYearStartMonth);
  const month = referenceDate.getMonth() + 1;
  const calendarYear = referenceDate.getFullYear();
  const fiscalYear = month >= startMonth ? calendarYear : calendarYear - 1;
  const monthInFiscalYear = month >= startMonth ? month - startMonth : month - startMonth + 12;
  const fiscalQuarter = Math.floor(monthInFiscalYear / 3) + 1;
  return { fiscalYear, fiscalQuarter };
}

export function fiscalQuarterDateRange(
  fiscalYear: number,
  fiscalQuarter: number,
  config: FiscalCalendarConfig = DEFAULT_FISCAL_CALENDAR
): { period_start_date: string; period_end_date: string } {
  const startMonth = normalizeFiscalYearStartMonth(config.fiscalYearStartMonth);
  const startMonthIndex = startMonth - 1 + (fiscalQuarter - 1) * 3;
  const startCalYear = fiscalYear + Math.floor(startMonthIndex / 12);
  const startCalMonth = startMonthIndex % 12;
  const endMonthIndex = startMonthIndex + 2;
  const endCalYear = fiscalYear + Math.floor(endMonthIndex / 12);
  const endCalMonth = endMonthIndex % 12;
  const startDate = new Date(startCalYear, startCalMonth, 1);
  const endDate = new Date(endCalYear, endCalMonth + 1, 0);
  return {
    period_start_date: formatLocalDate(startDate),
    period_end_date: formatLocalDate(endDate),
  };
}

export function formatFiscalYearLabel(
  fiscalYear: number,
  config: FiscalCalendarConfig = DEFAULT_FISCAL_CALENDAR
): string {
  const startMonth = normalizeFiscalYearStartMonth(config.fiscalYearStartMonth);
  if (startMonth === 1) return `FY ${fiscalYear}`;
  const endYear = fiscalYear + 1;
  return `FY ${fiscalYear}–${String(endYear).slice(-2)}`;
}
